import {
  Component,
  Output,
  EventEmitter,
  Inject,
  PLATFORM_ID,
  OnInit
} from '@angular/core';
import { GovernoratesService } from '../../../../../services/governorates.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import Swal from 'sweetalert2';
import { UserAddresses } from '../../../../../services/userAddresses';
import { Address } from '../../../../../interfaces/address';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-addaddress',
  templateUrl: './addaddress.component.html',
  styleUrl: './addaddress.component.scss'
})
export class AddaddressComponent implements OnInit {

  @Output() fees = new EventEmitter<any>();

  deliveryFees!: any;
  governorates: any[] = [];
  cities: any[] = [];

  isAr = false;
  contentReady = true;
  isCreateOrder = false;

  addressForm!: FormGroup;

  // ===== Google Maps =====
  center: google.maps.LatLngLiteral = { lat: 30.0444, lng: 31.2357 };
  markerPosition: google.maps.LatLngLiteral | null = null;

  markerOptions: google.maps.MarkerOptions = {
    draggable: true,
    icon: {
      url: 'assets/e-commerce/icons/54.webp',
      scaledSize: new google.maps.Size(50, 50),
      origin: new google.maps.Point(0, 0),
      anchor: new google.maps.Point(25, 50)
    }
  };

  mapOptions: google.maps.MapOptions = {
    disableDefaultUI: true,
    fullscreenControl: true,
    styles: [
      { elementType: 'geometry', stylers: [{ color: '#f1f3f6' }] },
      { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
      { elementType: 'labels.text.fill', stylers: [{ color: '#2a4da2' }] },
      { elementType: 'labels.text.stroke', stylers: [{ color: '#f1f3f6' }] },
      { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#f0d7ab' }] },
      { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#80bdeb' }] }
    ]
  };

  private isBrowser: boolean;

  constructor(
    private governoratesService: GovernoratesService,
    private addressesService: UserAddresses,
    private router: Router,
    private fb: FormBuilder,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  // ================= INIT =================
  ngOnInit(): void {

    // ⛔ SSR يقف هنا
    if (!this.isBrowser) {
      this.initForm();
      return;
    }

    this.isAr = localStorage.getItem('language') === 'ar';

    this.initForm();
    this.loadGovernorates();

    if (this.router.url === '/Addorderclient') {
      this.isCreateOrder = true;
    }
  }

  // ================= FORM =================
  initForm(): void {
    this.addressForm = this.fb.group({
      address_title: [''],
      address: ['', Validators.required],
      city_id: ['', Validators.required],
      government_id: ['', Validators.required],
      location_link: [''],
      location_latitude: [''],
      location_longitude: [''],
    });
  }

  // ================= GOVERNORATES =================
  loadGovernorates(): void {
    this.contentReady = true;
    this.governoratesService.getAllGovernorates().subscribe({
      next: (data) => {
        this.governorates = data;
        this.contentReady = false;
      },
      error: (err) => {
        console.error(err);
        this.contentReady = false;
      },
    });
  }

  loadCities(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    const governorateId = Number(selectElement.value);

    if (!governorateId) {
      this.cities = [];
      return;
    }

    this.getPrice(governorateId);

    this.governoratesService.getCity(governorateId).subscribe({
      next: (res) => {
        this.cities = res;
        this.contentReady = false;
      },
      error: (err) => {
        console.error(err);
        this.cities = [];
        this.contentReady = false;
      }
    });
  }

  getPrice(id: number): void {
    this.contentReady = true;
    this.governoratesService.getGovernoratePrice(id).subscribe({
      next: (res) => {
        this.deliveryFees = res.price;
        this.fees.emit(this.deliveryFees);
        this.contentReady = false;
      },
      error: (err) => {
        console.error(err);
        this.contentReady = false;
      }
    });
  }

  // ================= MAP =================
  onMapClick(event: google.maps.MapMouseEvent): void {
    if (!this.isBrowser || !event.latLng) return;

    this.markerPosition = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng()
    };
    this.updateLocationLink();
  }

  onMarkerDragEnd(event: google.maps.MapMouseEvent): void {
    if (!this.isBrowser || !event.latLng) return;

    this.markerPosition = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng()
    };
    this.updateLocationLink();
  }

  updateLocationLink(): void {
    if (!this.markerPosition) return;

    const { lat, lng } = this.markerPosition;
    const url = `https://www.google.com/maps?q=${lat},${lng}`;

    this.addressForm.patchValue({
      location_link: url,
      location_latitude: lat,
      location_longitude: lng
    });
  }

  getCurrentLocation(): void {
    if (!this.isBrowser || !navigator.geolocation) {
      Swal.fire({
        icon: 'error',
        title: this.isAr ? 'خطأ' : 'Error',
        text: this.isAr ? 'المتصفح لا يدعم تحديد الموقع' : 'Geolocation not supported'
      });
      return;
    }

    this.contentReady = true;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        this.center = { lat, lng };
        this.markerPosition = { lat, lng };

        this.addressForm.patchValue({
          location_latitude: lat,
          location_longitude: lng,
          location_link: `https://www.google.com/maps?q=${lat},${lng}`
        });

        this.contentReady = false;
      },
      (error) => {
        console.error(error);
        this.contentReady = false;
        Swal.fire({
          icon: 'error',
          title: this.isAr ? 'خطأ' : 'Error',
          text: this.isAr ? 'تعذر الحصول على الموقع الحالي' : 'Unable to get current location'
        });
      }
    );
  }

  // ================= SUBMIT =================
  addAddress(): void {
    if (this.addressForm.invalid) {
      Swal.fire({
        icon: 'error',
        title: this.isAr ? 'خطأ' : 'Error',
        text: this.isAr
          ? 'من فضلك أدخل جميع الحقول المطلوبة'
          : 'Please fill in all required fields'
      });
      return;
    }

    this.contentReady = true;
    const newAddress: Address = this.addressForm.value;

    this.addressesService.createAddress(newAddress).subscribe({
      next: () => {
        this.contentReady = false;
        Swal.fire({
          icon: 'success',
          title: this.isAr ? 'تم' : 'Success',
          text: this.isAr
            ? 'تمت إضافة العنوان بنجاح'
            : 'Address added successfully'
        });
        this.addressForm.reset();
        this.router.navigate(['/address']);
      },
      error: (err) => {
        console.error(err);
        this.contentReady = false;
        Swal.fire({
          icon: 'error',
          title: this.isAr ? 'خطأ' : 'Error',
          text: this.isAr
            ? 'حدث خطأ أثناء إضافة العنوان'
            : 'An error occurred while adding the address'
        });
      }
    });
  }
}
